import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Funnel, RefreshCw, ArrowRightLeft, Plus } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { InventoryTabs } from "../components/InventoryTabs";
import { StockPositionFiltersModal } from "../components/StockPositionFiltersModal";
import { StockPositionTable } from "../components/StockPositionTable";
import { StockJournalFiltersModal } from "../components/StockJournalFiltersModal";
import { InventoryJournalTable } from "../components/InventoryJournalTable";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

// Helper to format currency
const formatCurrency = (val) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

export function InventoryManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("position");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Position tab dataset states
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    productId: "All",
    warehouse: "All",
    category: "All",
    deadStock: "All",
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Journal tab dataset states
  const [filteredJournal, setFilteredJournal] = useState([]);
  const [journalFilters, setJournalFilters] = useState({
    productId: "All",
    warehouse: "All",
    movementType: "All",
    startDate: "",
    endDate: "",
  });
  const [journalFilterModalOpen, setJournalFilterModalOpen] = useState(false);
  const [journalPage, setJournalPage] = useState(1);
  const JOURNAL_PAGE_SIZE = 10;

  // Load all raw items once for filters options lists extraction
  useEffect(() => {
    let active = true;
    setLoading(true);

    Promise.all([
      inventoryManagementService.getStockPositions({}),
      inventoryManagementService.getStockJournal({}),
    ])
      .then(([positions, journal]) => {
        if (!active) return;
        setRawData(positions);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load initial inventory datasets:", err);
        setError(t("validation.failedToRetrieveInventoryData"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [t]);

  // Fetch filtered Stock Positions
  useEffect(() => {
    if (activeTab !== "position") return;
    let active = true;
    setLoading(true);

    inventoryManagementService.getStockPositions(filters)
      .then((res) => {
        if (!active) return;
        setFilteredData(res);
        setPage(1); // Reset page to first
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter stock positions:", err);
        setError(t("validation.failedToRetrieveStockPositions"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filters, activeTab, t]);

  // Fetch filtered Stock Journal
  useEffect(() => {
    if (activeTab !== "journal") return;
    let active = true;
    setLoading(true);

    inventoryManagementService.getStockJournal(journalFilters)
      .then((res) => {
        if (!active) return;
        setFilteredJournal(res);
        setJournalPage(1); // Reset page to first
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter stock journal:", err);
        setError(t("validation.failedToRetrieveStockJournal"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [journalFilters, activeTab, t]);

  // Extract unique products
  const productOptions = useMemo(() => {
    const map = new Map();
    rawData.forEach((item) => {
      if (!map.has(item.productId)) {
        map.set(item.productId, item.productName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [rawData]);

  // Extract unique warehouses
  const warehouseOptions = useMemo(() => {
    const set = new Set(rawData.map((item) => item.warehouse).filter(Boolean));
    return Array.from(set).sort();
  }, [rawData]);

  // Extract unique categories
  const categoryOptions = useMemo(() => {
    const set = new Set(rawData.map((item) => item.category).filter(Boolean));
    return Array.from(set).sort();
  }, [rawData]);

  // Compute active filters count (Position)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.productId !== "All") count++;
    if (filters.warehouse !== "All") count++;
    if (filters.category !== "All") count++;
    if (filters.deadStock !== "All") count++;
    return count;
  }, [filters]);

  // Compute active filters count (Journal)
  const activeJournalFilterCount = useMemo(() => {
    let count = 0;
    if (journalFilters.productId !== "All") count++;
    if (journalFilters.warehouse !== "All") count++;
    if (journalFilters.movementType !== "All") count++;
    if (journalFilters.startDate) count++;
    if (journalFilters.endDate) count++;
    return count;
  }, [journalFilters]);

  // Compute overall positions totals
  const kpiStats = useMemo(() => {
    let stockValueSum = 0;
    let reservedSum = 0;
    let itemsBelowReorder = 0;

    filteredData.forEach((item) => {
      stockValueSum += item.stockValue;
      reservedSum += item.reservedStock;
      if (item.availableStock <= item.reorderQuantity) {
        itemsBelowReorder++;
      }
    });

    return {
      stockValue: stockValueSum,
      reserved: reservedSum,
      belowReorder: itemsBelowReorder,
      pendingRevokes: filteredData.length > 0 ? 1 : 0,
    };
  }, [filteredData]);

  const handleApplyFilters = useCallback((nextFilters) => {
    setFilters(nextFilters);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      productId: "All",
      warehouse: "All",
      category: "All",
      deadStock: "All",
    });
  }, []);

  const handleApplyJournalFilters = useCallback((nextFilters) => {
    setJournalFilters(nextFilters);
  }, []);

  const handleResetJournalFilters = useCallback(() => {
    setJournalFilters({
      productId: "All",
      warehouse: "All",
      movementType: "All",
      startDate: "",
      endDate: "",
    });
  }, []);

  const handleStockTransfer = () => {
    navigate("/inventory-management/stock-transfer");
  };

  const handleAllocate = () => {
    navigate("/inventory-management/allocate-to-customer");
  };

  const handleRecordIn = () => {
    navigate("/inventory-management/record-inventory-in");
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        { label: t("inventory.inventoryManagement") },
      ]}
      className="space-y-6"
    >
      {/* Page Header (No actions next to title) */}
      <PageHeader
        title={t("inventory.inventoryManagement")}
      />

      {/* Tabs Switcher */}
      <InventoryTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Screen 1: Stock Position Tab */}
      {activeTab === "position" && (
        <div className="space-y-6">
          {/* KPI Dashboard Cards Grid (Compact Heights) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 font-sans">
            {/* Card 1: Stock Value */}
            <div className="rounded-xl border border-[var(--color-text-border)] bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.stockValue")}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                  {loading ? "..." : formatCurrency(kpiStats.stockValue)}
                </span>
              </div>
              <span className="text-[11px] text-[var(--color-text-secondary)] mt-2 font-medium">
                {t("inventory.stockValueHint")}
              </span>
            </div>

            {/* Card 2: Reserved Units */}
            <div className="rounded-xl border border-[var(--color-text-border)] bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.reservedUnits")}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                  {loading ? "..." : kpiStats.reserved.toLocaleString()}
                </span>
              </div>
              <span className="text-[11px] text-[var(--color-text-secondary)] mt-2 font-medium">
                {t("inventory.reservedUnitsHint")}
              </span>
            </div>

            {/* Card 3: Items Below Reorder */}
            <div className="rounded-xl border border-[var(--color-text-border)] bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.itemsBelowReorder")}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-amber-600 block mt-1 font-mono">
                  {loading ? "..." : kpiStats.belowReorder}
                </span>
              </div>
              <span className="text-[11px] text-[var(--color-text-secondary)] mt-2 font-medium">
                {t("inventory.itemsBelowReorderHint")}
              </span>
            </div>

            {/* Card 4: Pending Revokes */}
            <div className="rounded-xl border border-[var(--color-text-border)] bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.pendingRevokes")}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-[var(--color-primary)] block mt-1 font-mono">
                  {loading ? "..." : kpiStats.pendingRevokes}
                </span>
              </div>
              <span className="text-[11px] text-[var(--color-text-secondary)] mt-2 font-medium">
                {t("inventory.pendingRevokesHint")}
              </span>
            </div>
          </div>

          {/* Table Container Card */}
          <ContentCard className="overflow-hidden">
            {/* Attached Header Panel */}
            <div className="px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
              <span className="text-xs font-bold text-foreground">{t("inventory.stockPositionsOverview")}</span>

              <div className="flex items-center gap-2">
                {/* Filter Modal Trigger */}
                <button
                  type="button"
                  onClick={() => setFilterModalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition cursor-pointer"
                >
                  <Funnel className="h-3.5 w-3.5" />
                  <span>{t("formActions.filter")}</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Reset filters */}
                <button
                  type="button"
                  onClick={handleResetFilters}
                  disabled={activeFilterCount === 0}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                  <span>{t("formActions.resetFilters")}</span>
                </button>

                {/* Stock Transfer */}
                <button
                  type="button"
                  onClick={handleStockTransfer}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 transition cursor-pointer"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                  <span>{t("inventory.stockTransfer")}</span>
                </button>
              </div>
            </div>

            {loading ? (
              /* Custom beautiful Skeleton Loader */
              <div className="p-6 space-y-4 animate-pulse">
                <div className="h-10 bg-gray-100 rounded-lg w-full" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="h-14 bg-gray-50/50 rounded-lg w-full flex items-center justify-between px-4">
                      <div className="h-4 bg-gray-100 rounded w-1/4" />
                      <div className="h-4 bg-gray-100 rounded w-1/6" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              /* Error Banner */
              <div className="p-8 text-center text-[var(--color-error)] font-semibold text-sm">
                ⚠️ {error}
              </div>
            ) : filteredData.length === 0 ? (
              /* Empty state matching specifications */
              <div className="p-12 text-center font-sans">
                <div className="h-12 w-12 rounded-full bg-[var(--color-workspace)] text-[var(--color-text-secondary)] grid place-items-center mx-auto mb-4">
                  <span>📦</span>
                </div>
                <h4 className="text-sm font-bold text-foreground">{t("inventory.noStockRecordsFound")}</h4>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1.5 leading-relaxed max-w-xs mx-auto">
                  {t("inventory.noStockRecordsHint")}
                </p>
              </div>
            ) : (
              /* Stock Positions Table */
              <StockPositionTable
                data={filteredData}
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </ContentCard>
        </div>
      )}

      {/* Screen 2: Stock Journal Tab */}
      {activeTab === "journal" && (
        <div className="space-y-6">
          {/* Table Container Card */}
          <ContentCard className="overflow-hidden">
            {/* Attached Header Panel (Filters and Actions integrated) */}
            <div className="px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
              <span className="text-xs font-bold text-foreground">{t("inventory.stockJournalLogs")}</span>

              <div className="flex items-center gap-2">
                {/* Filter Modal Trigger */}
                <button
                  type="button"
                  onClick={() => setJournalFilterModalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition cursor-pointer"
                >
                  <Funnel className="h-3.5 w-3.5" />
                  <span>{t("formActions.filter")}</span>
                  {activeJournalFilterCount > 0 && (
                    <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                      {activeJournalFilterCount}
                    </span>
                  )}
                </button>

                {/* Reset filters */}
                <button
                  type="button"
                  onClick={handleResetJournalFilters}
                  disabled={activeJournalFilterCount === 0}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                  <span>{t("formActions.resetFilters")}</span>
                </button>

                {/* Allocate to Customer */}
                <button
                  type="button"
                  onClick={handleAllocate}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 transition cursor-pointer shadow-sm"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                  <span>{t("inventory.allocateToCustomer")}</span>
                </button>

                {/* Record Inventory In */}
                <button
                  type="button"
                  onClick={handleRecordIn}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-bold hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>{t("inventory.recordInventoryIn")}</span>
                </button>
              </div>
            </div>

            {loading ? (
              /* Skeleton Loader */
              <div className="p-6 space-y-4 animate-pulse">
                <div className="h-10 bg-gray-100 rounded-lg w-full" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="h-12 bg-gray-50/50 rounded-lg w-full flex items-center gap-4 px-4">
                      <div className="h-4 bg-gray-100 rounded w-20" />
                      <div className="h-4 bg-gray-100 rounded w-24" />
                      <div className="h-4 bg-gray-100 rounded w-1/4" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-[var(--color-error)] font-semibold text-sm">
                ⚠️ {error}
              </div>
            ) : filteredJournal.length === 0 ? (
              /* Empty state matching specifications */
              <div className="p-12 text-center font-sans">
                <div className="h-12 w-12 rounded-full bg-[var(--color-workspace)] text-[var(--color-text-secondary)] grid place-items-center mx-auto mb-4">
                  <span>🗒️</span>
                </div>
                <h4 className="text-sm font-bold text-foreground">{t("inventory.noInventoryMovementsFound")}</h4>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1.5 leading-relaxed max-w-sm mx-auto mb-6">
                  {t("inventory.noInventoryMovementsHint")}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={handleAllocate}
                    className="h-10 px-4 rounded-xl border border-[var(--color-text-border)] text-xs font-bold hover:bg-gray-50 cursor-pointer"
                  >
                    {t("inventory.allocateToCustomer")}
                  </button>
                  <button
                    type="button"
                    onClick={handleRecordIn}
                    className="h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white text-xs font-bold hover:bg-[var(--color-accent)] transition cursor-pointer"
                  >
                    {t("inventory.recordInventoryIn")}
                  </button>
                </div>
              </div>
            ) : (
              /* Stock Journal Audit Table */
              <InventoryJournalTable
                data={filteredJournal}
                page={journalPage}
                pageSize={JOURNAL_PAGE_SIZE}
                onPageChange={setJournalPage}
              />
            )}
          </ContentCard>
        </div>
      )}

      {/* Stock Position Filters Modal */}
      <StockPositionFiltersModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        appliedFilters={filters}
        onApply={handleApplyFilters}
        productOptions={productOptions}
        warehouseOptions={warehouseOptions}
        categoryOptions={categoryOptions}
      />

      {/* Stock Journal Filters Modal */}
      <StockJournalFiltersModal
        open={journalFilterModalOpen}
        onOpenChange={setJournalFilterModalOpen}
        appliedFilters={journalFilters}
        onApply={handleApplyJournalFilters}
        productOptions={productOptions}
        warehouseOptions={warehouseOptions}
      />
    </AppLayout>
  );
}
